import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useObrasContext, Obra } from "@/contexts/ObrasContext";
import { toast } from "@/hooks/use-toast";

interface Cliente {
  id: string;
  nombre: string;
}

const obraSchema = z.object({
  nombre: z.string().min(1, "El nombre es requerido"),
  descripcion: z.string().optional(),
  direccion: z.string().optional(),
  numero_presupuesto: z.string().optional().nullable(),
  cliente_id: z.string().min(1, "Debe seleccionar un cliente"),
  horas_estimadas: z.number().min(0, "Las horas estimadas deben ser positivas"),
  coste_mano_obra_estimado: z.number().min(0, "El importe debe ser positivo"),
  precio_venta: z.number().min(0).optional().nullable(),
  estado: z.enum(['activa', 'pausada', 'finalizada', 'cancelada']),
  fecha_inicio: z.date(),
  fecha_fin_estimada: z.date().optional(),
});

type ObraFormValues = z.infer<typeof obraSchema>;

interface ObraFormProps {
  obra?: Obra;
  onSuccess: () => void;
}

export function ObraForm({ obra, onSuccess }: ObraFormProps) {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const { createObra, updateObra } = useObrasContext();

  const form = useForm<ObraFormValues>({
    resolver: zodResolver(obraSchema),
    defaultValues: {
      nombre: obra?.nombre || "",
      descripcion: obra?.descripcion || "",
      direccion: obra?.direccion || "",
      numero_presupuesto: obra?.numero_presupuesto || null,
      cliente_id: obra?.cliente_id || "",
      horas_estimadas: obra?.horas_estimadas || 0,
      coste_mano_obra_estimado: obra?.coste_mano_obra_estimado || 0,
      precio_venta: obra?.precio_venta ?? null,
      estado: obra?.estado || 'activa',
      fecha_inicio: obra ? new Date(obra.fecha_inicio) : new Date(),
      fecha_fin_estimada: obra?.fecha_fin_estimada ? new Date(obra.fecha_fin_estimada) : undefined,
    },
  });

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('id, nombre, tipo_contacto, activo')
        .in('tipo_contacto', ['cliente', 'ambos'])
        .eq('activo', true)
        .order('nombre');

      if (error) throw error;
      setClientes(data || []);
    } catch (error) {
      console.error('Error loading clientes:', error);
      toast({
        title: "Error",
        description: "Error al cargar los clientes",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (values: ObraFormValues) => {
    setLoading(true);
    try {
      const obraData = {
        ...values,
        fecha_inicio: format(values.fecha_inicio, 'yyyy-MM-dd'),
        fecha_fin_estimada: values.fecha_fin_estimada 
          ? format(values.fecha_fin_estimada, 'yyyy-MM-dd')
          : undefined,
      };

      if (obra) {
        await updateObra(obra.id, obraData as any);
      } else {
        await createObra(obraData as any);
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving obra:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="nombre"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre de la Obra</FormLabel>
                    <FormControl>
                      <Input placeholder="Ej: Instalación sistema alarma" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="numero_presupuesto"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nº Presupuesto / Oferta</FormLabel>
                    <FormControl>
                      <Input placeholder="Ej: OF-2025-001" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cliente_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <SearchableSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={clientes.map((c) => ({
                        value: c.id,
                        label: c.nombre,
                        searchText: c.nombre,
                      }))}
                      placeholder="Seleccionar cliente"
                      searchPlaceholder="Buscar cliente..."
                      emptyMessage="No se encontraron clientes"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="horas_estimadas"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Horas estimadas</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          placeholder="0"
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="coste_mano_obra_estimado"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Presupuesto mano de obra (€)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="precio_venta"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Precio de venta al cliente (€)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Opcional — necesario para calcular el margen"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="text-xs text-muted-foreground -mt-2">
                Introduce horas, importe, o ambos. El precio de venta sirve para calcular el margen real al finalizar la obra.
              </p>

              <FormField
                control={form.control}
                name="estado"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="activa">Activa</SelectItem>
                        <SelectItem value="pausada">Pausada</SelectItem>
                        <SelectItem value="finalizada">Finalizada</SelectItem>
                        <SelectItem value="cancelada">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="fecha_inicio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha de Inicio</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button variant="outline" className="w-full pl-3 text-left font-normal">
                            {field.value ? (
                              format(field.value, "dd/MM/yyyy", { locale: es })
                            ) : (
                              <span className="text-muted-foreground">Seleccionar fecha</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          locale={es}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="fecha_fin_estimada"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha Fin Estimada (Opcional)</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button variant="outline" className="w-full pl-3 text-left font-normal">
                            {field.value ? (
                              format(field.value, "dd/MM/yyyy", { locale: es })
                            ) : (
                              <span className="text-muted-foreground">Seleccionar fecha</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          locale={es}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="direccion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dirección</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej: C/ Mayor 12, Madrid" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="descripcion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descripción detallada de la obra..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={onSuccess}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Guardando..." : obra ? "Actualizar Obra" : "Crear Obra"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}